import { supabase } from '../supabaseClient';
import { getOutcomeConfig } from './statusBadge';

// Outcome codes that mean the patient has confirmed an appointment — when one
// of these is logged on an unassigned draw, the phleb implicitly takes
// ownership (auto-claim). Keeps claim ownership tied to actual commitment
// instead of a separate "Claim" button click.
export const CONFIRMING_OUTCOMES = new Set(['scheduled', 'appointment_confirmed']);

/**
 * Write a disposition for a submission: update the submissions row and append a
 * row to the submission_status_events audit log. Shared by OutcomeDrawer and
 * the phleb summary stepper so both paths produce identical data.
 *
 * @param {object}  args
 * @param {object}  args.submission     - the submissions row (needs id + phlebotomist_id)
 * @param {string}  args.code           - outcome code from OUTCOME_CONFIG
 * @param {string} [args.note]          - optional free-text note
 * @param {string[]} [args.flags]       - optional outcome attribute flags (difficult_stick, partial_draw, …);
 *                                         written to outcome_flags and reset on every disposition.
 * @param {string} [args.scheduledIso]  - optional ISO time to set blood_collection_time (legacy exact-time path)
 * @param {object} [args.schedule]      - optional schedule payload from buildSchedulePayload() in utils/schedule;
 *                                         carries scheduling_type + availability_* + blood_collection_time. Takes
 *                                         precedence over scheduledIso when present.
 * @param {string} [args.phlebotomistId]- logged-in phleb id (drives the phleb default + auto-claim)
 * @param {string} [args.loggedByKind]  - explicit actor kind ('phleb' | 'admin' | 'lab'); overrides the phleb default
 * @param {string} [args.loggedById]    - explicit actor id; overrides phlebotomistId for the stamp
 * @param {string} [args.loggedByName]  - explicit actor display name; snapshotted into logged_by_name on the event
 * @returns {Promise<{updateFields: object}>} the fields written to the submissions row
 * @throws if the outcome code is unknown or the main update fails
 */
/**
 * Append an "assigned" / "unassigned" event to a submission's timeline.
 *
 * Assignment is written straight to submissions.phlebotomist_id from several
 * places (admin assign, phleb claim, route reassignment, auto-claim), none of
 * which historically left a trace. This records the transition so the activity
 * timeline shows who a draw was assigned to and when.
 *
 * Only writes to submission_status_events — never to submissions.status or
 * outcome_code — so it can't be mistaken for a disposition or move the
 * current-status card / LDX lifecycle. Best-effort: logs a warning on failure
 * rather than throwing, so a timeline hiccup never blocks the actual assignment.
 *
 * @param {object}  args
 * @param {number}  args.submissionId
 * @param {string} [args.fromName]   display name the draw was assigned to before (null if it was unassigned)
 * @param {string} [args.toName]     display name it's assigned to now (null/'' means it was cleared)
 * @param {string} [args.actorKind]  'admin' | 'phleb' | 'lab' — who made the change
 * @param {string} [args.actorId]    actor id for logged_by
 * @param {string} [args.actorName]  actor display name, snapshotted into logged_by_name
 */
export async function logAssignmentChange({ submissionId, fromName, toName, actorKind = 'admin', actorId = null, actorName = null }) {
  if (!submissionId) return;
  const assigned = !!(toName && String(toName).trim());
  const code = assigned ? 'assigned' : 'unassigned';
  const from = fromName && String(fromName).trim() ? String(fromName).trim() : null;
  const to = assigned ? String(toName).trim() : null;

  let note;
  if (assigned && from && from !== to) note = `Reassigned from ${from} to ${to}`;
  else if (assigned) note = `Assigned to ${to}`;
  else if (from) note = `Unassigned from ${from}`;
  else note = 'Unassigned';

  const baseRow = {
    submission_id: submissionId,
    outcome_code: code,
    outcome_notes: note,
    status: null, // system event: never changes the submission's status
    logged_by: actorId,
    logged_by_kind: actorKind,
  };

  // Same logged_by_name column-missing fallback as below, so this works whether
  // or not add_logged_by_name.sql has been applied.
  if (actorName) {
    const res = await supabase
      .from('submission_status_events')
      .insert({ ...baseRow, logged_by_name: actorName });
    if (res.error && /logged_by_name/i.test(res.error.message || '')) {
      const retry = await supabase.from('submission_status_events').insert(baseRow);
      if (retry.error) console.warn('Could not write assignment event:', retry.error.message);
    } else if (res.error) {
      console.warn('Could not write assignment event:', res.error.message);
    }
  } else {
    const res = await supabase.from('submission_status_events').insert(baseRow);
    if (res.error) console.warn('Could not write assignment event:', res.error.message);
  }
}

export async function logSubmissionOutcome({ submission, code, note, flags, scheduledIso, schedule, phlebotomistId, loggedByKind, loggedById, loggedByName }) {
  if (!submission?.id) throw new Error('No submission selected');
  const cfg = getOutcomeConfig(code);
  if (!cfg) throw new Error('Unknown outcome');

  // Who is logging this. Callers can pass loggedByKind/loggedById explicitly
  // (e.g. the lab portal stamps 'lab'); otherwise it's a phleb when a
  // phlebotomistId is present, else an admin.
  const actorId = loggedById ?? phlebotomistId ?? null;
  const actorKind = loggedByKind ?? (phlebotomistId ? 'phleb' : 'admin');
  // Snapshot the actor's display name into the event row so the timeline
  // doesn't need to join back on admin_users / phlebotomist_profiles at
  // render time. logged_by_name column was added in add_logged_by_name.sql.
  const actorName = loggedByName || null;

  const targetId = submission.id;
  const nowIso = new Date().toISOString();
  const updateFields = {
    outcome_code: code,
    outcome_notes: note || null,
    outcome_logged_at: nowIso,
    outcome_logged_by: actorId,
    followup_required: !!cfg.followupRequired,
    followup_reason: cfg.followupRequired ? cfg.label : null,
    status: cfg.nextStatus,
    // Reset attribute flags on every disposition — they belong to the outcome
    // just logged, so a later outcome with no flags clears stale ones.
    outcome_flags: Array.isArray(flags) ? flags : [],
  };
  // Actual draw date (our side): stamp when a Successful Draw is logged. This
  // is what invoices use as the draw date, independent of the requested time.
  if (code === 'successful_draw') {
    updateFields.drawn_at = nowIso;
  }

  // Schedule: the structured `schedule` payload (exact/window/all_day) wins;
  // otherwise fall back to the legacy single-time `scheduledIso`.
  const scheduleFields = schedule && typeof schedule === 'object' ? schedule : null;
  if (scheduleFields) {
    Object.assign(updateFields, scheduleFields);
  } else if (scheduledIso) {
    updateFields.blood_collection_time = scheduledIso;
  }

  // Auto-claim — a phleb takes ownership the moment the patient confirms an
  // appointment. Only applies to phleb actors; skipped for admin/lab or when
  // the draw is already assigned to someone.
  let didAutoClaim = false;
  if (CONFIRMING_OUTCOMES.has(code) && phlebotomistId && !submission.phlebotomist_id) {
    updateFields.phlebotomist_id = phlebotomistId;
    // Scheduling it is the phleb taking it on — counts as confirming it to their route.
    updateFields.route_confirmed_at = nowIso;
    didAutoClaim = true;
  }

  const { error } = await supabase.from('submissions').update(updateFields).eq('id', targetId);
  if (error) throw error;

  // Append-only history so admins/labs can see every disposition + note in
  // order. Best-effort — a failure here shouldn't undo the main update.
  // Try with logged_by_name (the new column); if Postgres rejects because the
  // migration hasn't been applied yet, fall back to the legacy shape so
  // existing deployments keep working.
  const baseRow = {
    submission_id: targetId,
    outcome_code: code,
    outcome_notes: note || null,
    outcome_flags: Array.isArray(flags) ? flags : null,
    status: cfg.nextStatus,
    blood_collection_time: (scheduleFields ? scheduleFields.blood_collection_time : scheduledIso) || null,
    scheduling_type: scheduleFields ? scheduleFields.scheduling_type : null,
    availability_start_date: scheduleFields ? scheduleFields.availability_start_date : null,
    availability_end_date: scheduleFields ? scheduleFields.availability_end_date : null,
    availability_start_time: scheduleFields ? scheduleFields.availability_start_time : null,
    availability_end_time: scheduleFields ? scheduleFields.availability_end_time : null,
    logged_by: actorId,
    logged_by_kind: actorKind,
  };
  let eventError = null;
  if (actorName) {
    const res = await supabase
      .from('submission_status_events')
      .insert({ ...baseRow, logged_by_name: actorName });
    if (res.error && /logged_by_name/i.test(res.error.message || '')) {
      // Column missing — retry without it so logging still succeeds.
      const retry = await supabase.from('submission_status_events').insert(baseRow);
      eventError = retry.error;
    } else {
      eventError = res.error;
    }
  } else {
    const res = await supabase.from('submission_status_events').insert(baseRow);
    eventError = res.error;
  }
  if (eventError) console.warn('Could not write status event:', eventError.message);

  // Auto-claim is a silent assignment (phleb scheduled an unassigned draw and
  // implicitly took it). Record it on the timeline too, right after the
  // disposition, so the assignment isn't invisible.
  if (didAutoClaim) {
    await logAssignmentChange({
      submissionId: targetId,
      fromName: null,
      toName: actorName || 'the phlebotomist',
      actorKind: actorKind,
      actorId: actorId,
      actorName: actorName,
    });
  }

  return { updateFields };
}
